import type { Session } from "@backchannel/tax-contracts";
import { TaxAuthShell } from "~/components/auth/tax-auth-shell";
import { QueryErrorState } from "~/components/ui/states";
import { useT } from "~/i18n";
import { SessionProvider } from "~/lib/access/session-context";
import { useAuth } from "~/lib/auth/auth-context";

/**
 * Keeps an authenticated operator inside the product without pretending that
 * the workspace is ready. The navigation shell is deliberately not rendered
 * until `/v1/session` returns a validated principal; a Cognito identity alone
 * is not enough to make any workspace route navigable.
 */
export function WorkspaceErrorShell({
  error,
  onRetry,
  session = null,
}: {
  error: unknown;
  onRetry: () => void;
  /** Preserve a previously validated read-model while a refresh is failing. */
  session?: Session | null;
}) {
  const t = useT();
  const auth = useAuth();

  return (
    <SessionProvider session={session ?? null}>
      <TaxAuthShell title={t("auth.sessionUnavailable")} variant="recovery">
        <section className="workspace-recovery" aria-describedby="workspace-recovery-description">
          <p id="workspace-recovery-description" className="workspace-recovery__description">
            {t("auth.sessionUnavailableDescription")}
          </p>
          <QueryErrorState error={error} />
          <div className="workspace-recovery__actions">
            <button type="button" className="btn btn--primary" onClick={onRetry}>
              {t("common.retry")}
            </button>
            <button type="button" className="btn" onClick={auth.signOut}>
              {t("auth.signOut")}
            </button>
          </div>
        </section>
      </TaxAuthShell>
    </SessionProvider>
  );
}
